import { ReactElement } from 'react';
import { useField } from 'formik';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import withComponentLoader from '../../../../hoc/withComponentLoader';
import AppWrappedInput from './AppWrappedInput';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

/**
 * Searchable text input base component stylized to work with Custom App Request Form
 * @param {boolean} props.isRequired flag to determine whether to show required indicator
 * @param {Object[]} props.originalOptions list of options to display.  Should be list of objects matching `{[key]: [value]}` pattern
 * @param {string} props.label display text
 * @param {string} props.sublabel optional secondary display text to show as default placeholder on input
 * @param {string|ReactElement} props.helperText helper text
 * @param {boolean} props.isDisabled flag to determine whether input is disabled
 * @param {boolean} props.isLoading flag to indicate the loading state (comes from parent)
 * @param {Function} props.setLoading setter function coming from `withComponentLoader` HOC
 * @param {Function} props.getValue function to determine the proper value to display for repopulation on render
 * @param {object} props.props the rest of the props
 * @returns Searchable text input stylized for custom
 */
const AppWrappedStaticAutoComplete = ({
	isRequired,
	originalOptions = [],
	label,
	sublabel,
	helperText,
	isDisabled = false,
	isLoading = false,
	multiple = false,
	getValue = (options, value) =>
		multiple
			? options.filter(o => value?.includes(o.value))
			: options.find(o => o.value === value),
	setLoading,
	...props
}) => {
	const [field, meta, helpers] = useField(props);
	const [options, setOptions] = useState([...originalOptions]);
	const [sInputValue, setInputValue] = useState('');
	setLoading(isLoading);

	return (
		<AppWrappedInput
			label={label}
			error={meta.touched ? meta.error : null}
			isRequired={isRequired}
		>
			<FormControl variant='filled' fullWidth>
				<Autocomplete
					autoComplete
					multiple={multiple}
					value={
						multiple
							? getValue(originalOptions, field.value)
							: field.value
								? getValue(originalOptions, field.value)
								: null
					}
					clearIcon={null}
					disableClearable={!multiple}
					filterOptions={options =>
						multiple
							? options.filter(
									o => !field.value?.includes(o.value),
								)
							: options
					}
					forcePopupIcon={true}
					getOptionLabel={option => option.label}
					groupBy={option => option.group}
					isOptionEqualToValue={(option, value) => {
						return option.value === value;
					}}
					inputValue={multiple ? sInputValue : undefined}
					noOptionsText={'No results'}
					onChange={(_, newValue) => {
						if (multiple) {
							helpers.setValue(
								newValue ? newValue.map(o => o.value) : [],
							);
							setInputValue('');
							setOptions([...originalOptions]);
						} else if (newValue) {
							helpers.setValue(newValue.value);
						} else {
							helpers.setValue(null);
						}
					}}
					onInputChange={(_, newInputValue, reason) => {
						if (reason === 'input' || reason === 'clear') {
							setInputValue(newInputValue);
							setOptions(
								originalOptions.filter(
									o =>
										o.label
											?.toLocaleLowerCase()
											.includes(
												newInputValue.toLocaleLowerCase(),
											) ||
										o.group
											?.toLocaleLowerCase()
											.includes(
												newInputValue.toLocaleLowerCase(),
											),
								),
							);
						}
					}}
					options={options}
					renderInput={params => (
						<TextField
							{...params}
							fullWidth
							variant='filled'
							placeholder={
								sublabel
									? sublabel
									: 'Search for ' + label + '...'
							}
							name={props.name}
							InputLabelProps={{ shrink: false }}
							InputProps={{
								...params.InputProps,
								disableUnderline: true,
							}}
							onBlur={field.onBlur}
							disabled={isDisabled}
							{...props}
						/>
					)}
					renderOption={(props, option, { inputValue }) => {
						const optionStr = option.label ? option.label : option;
						const matches = match(optionStr, inputValue, {
							insideWords: true,
							findAllOccurrences: true,
						});
						const parts = parse(optionStr, matches);

						return (
							<li {...props}>
								<div>
									{parts.map((part, index) => (
										<span
											key={index}
											style={{
												fontWeight: part.highlight
													? 700
													: 400,
											}}
										>
											{part.text}
										</span>
									))}
								</div>
							</li>
						);
					}}
					onBlur={field.onBlur}
					onFocus={field.onFocus}
				/>
				<FormHelperText>{helperText}</FormHelperText>
			</FormControl>
		</AppWrappedInput>
	);
};
export default withComponentLoader(AppWrappedStaticAutoComplete);
